import { useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@wehunt/core-web'
import { MapCatalogueProvider, type MapCatalogue } from '@wehunt/ui'
import { apiFetch } from '../../shared/api/apiFetch'

/** One account/language-scoped query for the Map tab, previews and editor. */
export function AuthenticatedMapCatalogue({
  children,
}: {
  children: ReactNode
}) {
  const { state } = useAuth()
  const { i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? i18n.language
  const uid = state.status === 'signed-in' ? state.user.uid : null
  const query = useQuery({
    queryKey: ['clubhouse', 'map-catalogue', uid, language],
    enabled: uid !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      if (state.status !== 'signed-in')
        throw new Error('map catalogue: signed out')
      return apiFetch<MapCatalogue>(
        `/maps?language=${encodeURIComponent(language)}`,
        { token: await state.user.getIdToken() },
      )
    },
  })
  // Memoised so the map surfaces reading the catalogue re-render when it
  // changes, not every time the route above them does.
  const { data, isFetching, isPending, isError, refetch } = query
  const catalogue = useMemo(
    () => ({
      data: data ?? null,
      pending: isFetching || isPending,
      error: isError,
      retry: () => {
        void refetch()
      },
    }),
    [data, isFetching, isPending, isError, refetch],
  )
  return <MapCatalogueProvider value={catalogue}>{children}</MapCatalogueProvider>
}
